"use client";

import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { cn } from "@/app/utils/className";
import { Heading2 } from "../typography";

export interface BannerStat {
  value: string;
  label: string;
  color: "blue" | "green" | "orange";
}

interface BannerStatsProps {
  stats: BannerStat[];
  className?: string;
  animate?: boolean;
}

const colorConfig = {
  blue: {
    text: "text-haizea-blue",
    gradient: "from-haizea-blue to-haizea-green",
  },
  green: {
    text: "text-haizea-green",
    gradient: "from-haizea-green to-haizea-orange",
  },
  orange: {
    text: "text-haizea-orange",
    gradient: "from-haizea-orange to-haizea-blue",
  },
};

export const BannerStats = ({
  stats,
  className,
  animate = true,
}: BannerStatsProps) => {
  useGSAP(() => {
    if (!animate) return;

    gsap.fromTo(
      ".banner-stat",
      {
        y: 40,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        stagger: 0.15,
        duration: 0.8,
        ease: "power2.out",
        delay: 0.5,
      }
    );
  }, [animate]);

  return (
    <div
      className={cn(
        "flex md:grid gap-4 md:gap-6",
        {
          "md:grid-cols-2": stats.length === 2,
          "md:grid-cols-3": stats.length === 3,
          "md:grid-cols-4": stats.length >= 4,
        },
        className
      )}
    >
      {stats.map((stat, index) => {
        const colors = colorConfig[stat.color];
        return (
          <div
            key={index}
            className="banner-stat group relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-3 md:p-4 xl:px-6 hover:bg-white/15 transition-all duration-300 overflow-hidden"
          >
            <Heading2 className={cn("mb-0 md:mb-1 lg:mb-1", colors.text)}>
              {stat.value}
            </Heading2>
            <div className="text-sm md:text-base text-slate-200 font-medium">
              {stat.label}
            </div>
            <div
              className={cn(
                "absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                colors.gradient
              )}
            />
          </div>
        );
      })}
    </div>
  );
};
